import React, { useEffect, useMemo } from 'react';
import { useFormik } from 'formik';
import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { DatePicker } from 'syngenta-digital-cropwise-react-ui-kit';
import FilterDrawerShell from 'components/RecordsListView/FilterDrawerShell';
import useInvoiceEvents from '../hooks/useInvoiceEvents';
import { InvoiceFilters } from '../types';
import './InvoiceFilterDrawer.css';

const { RangePicker } = DatePicker;

export const INVOICE_FILTER_DEFAULTS: InvoiceFilters = {
  dateRange: []
};

const countActiveFilterGroups = (filters: InvoiceFilters): number => {
  let count = 0;
  if (filters.dateRange?.length === 2) count += 1;
  return count;
};

export { countActiveFilterGroups };

interface InvoiceFilterDrawerProps {
  visible: boolean;
  initialValues?: InvoiceFilters;
  onClose: () => void;
  onFiltersChange: (filters: InvoiceFilters) => void;
}

const InvoiceFilterDrawer: React.FC<InvoiceFilterDrawerProps> = ({
  visible,
  initialValues,
  onClose,
  onFiltersChange
}) => {
  const { sendApplyFiltersEvent } = useInvoiceEvents();
  const adjustedInitialValues = useMemo(
    () => initialValues ?? INVOICE_FILTER_DEFAULTS,
    [initialValues]
  );

  const formik = useFormik<InvoiceFilters>({
    initialValues: adjustedInitialValues,
    onSubmit: (values, actions) => {
      actions.setTouched({});
      onFiltersChange(values);
      actions.setSubmitting(false);
    }
  });

  const handleCancel = () => {
    formik.setValues(adjustedInitialValues);
    onClose();
  };

  useEffect(() => {
    if (!isEqual(formik.values, adjustedInitialValues)) {
      formik.setValues(adjustedInitialValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustedInitialValues]);

  const isCleared = isEqual(formik.values, INVOICE_FILTER_DEFAULTS);
  const isDirty = !isEqual(formik.values, adjustedInitialValues);

  return (
    <FilterDrawerShell
      visible={visible}
      title="Filter"
      className="invoice-filter-drawer"
      testId="invoice-filter-drawer"
      applyDisabled={!isDirty}
      clearAllDisabled={isCleared}
      onSubmit={formik.handleSubmit}
      onCancel={handleCancel}
      onApply={() => {
        sendApplyFiltersEvent(formik.values.dateRange?.length === 2);
        formik.handleSubmit();
        onClose();
      }}
      onClearAll={() => {
        Object.keys(formik.values).forEach(key => {
          formik.setFieldTouched(key, true);
        });
        formik.setValues(INVOICE_FILTER_DEFAULTS);
      }}
    >
      {/* Invoice date range — server-supported */}
      <div className="flex flex-col px-4 pb-5">
        <div className="flex grow py-5">
          <h3 className="font-semibold">Invoice Date</h3>
        </div>
        <div
          className="flex grow"
          id="invoice-filter-date-range-picker-container"
        >
          <RangePicker
            name="dateRange"
            onChange={(dates: [moment.Moment, moment.Moment | null]) => {
              formik.setFieldValue('dateRange', dates ?? []);
              formik.setFieldTouched('dateRange', true);
            }}
            value={formik.values.dateRange}
            className="w-full"
            format="MM/DD/YYYY"
          />
        </div>
      </div>
    </FilterDrawerShell>
  );
};

export default InvoiceFilterDrawer;
